<template>
	<section>
		<div class="content-devx margin-half" style="padding: 0">
			<div style="padding: 4px 4px 0 4px">
				<div class="main-title-content">
					APLICACIÓN DE PRODUCTOS EN CADA PERIODO
				</div>
			</div>

			<div style="padding: 0 4px">
				<div class="content-separatos">
					<DxDataGrid
						class="grid-application-efficiency-per-period"
						:data-source="dataSource"
						:show-borders="true"
						:row-alternation-enabled="false"
						:word-wrap-enabled="true"
						:column-auto-width="true"
						:show-row-lines="true"
						:show-column-lines="true"
						@cellPrepared="onCellPreparedViewing">
						<DxPaging :enabled="false" />
						<DxColumn
							data-field="name"
							caption="Producto"
							:allow-sorting="false"
							:width="350"
							:fixed="true"
							fixed-position="left"
							cell-template="productCellTemplate" />
						<DxColumn
							data-field="rowType"
							caption=""
							:allow-sorting="false"
							:width="80"
							:fixed="true"
							fixed-position="left"
							cell-template="rowTypeCellTemplate" />
						<DxColumn
							v-for="(col, idx) in getPeriods"
							:key="idx"
							v-bind="col"
							cell-template="dataCellTemplate" />
						<template #productCellTemplate="{ data }">
							<span v-if="data.data.rowType !== 'sectores'">
								{{ data.data.name }}
							</span>
						</template>
						<template #rowTypeCellTemplate="{ data }">
							<span>{{ data.value ? data.value.charAt(0).toUpperCase() + data.value.slice(1) : '' }}</span>
						</template>
						<template #dataCellTemplate="{ data }">
							<template v-if="data.data.rowType === 'sectores'">
								<div
									v-if="getSectorsForCell(getRawCellValue(data)).length > 0"
									class="sectors-container">
									<span
										v-for="(sector, idx) in getSectorsForCell(getRawCellValue(data))"
										:key="idx"
										class="sector-box">
										{{ sector }}
									</span>
								</div>
							</template>
							<span v-else>{{ data.value }}</span>
						</template>
					</DxDataGrid>
				</div>
			</div>
		</div>
	</section>
</template>
<script>
	import { DxDataGrid, DxColumn, DxPaging } from 'devextreme-vue/data-grid';
	import moment from 'moment';
	import { getTimeValues } from '../../../js/utils/timeValuesUtils';
	import { formatMonthLabel, sortMonths } from '../../../js/utils/months';
	import { mapGetters } from 'vuex';

	export default {
		name: 'ProductResumeTable',
		components: {
			DxDataGrid,
			DxColumn,
			DxPaging,
		},
		props: {
			fertilization: {
				type: Object,
				default: () => {},
			},
			isReferencePlan: {
				type: Boolean,
				default: false,
			},
		},
		data() {
			return {
				dataSource: [],
			};
		},
		computed: {
			...mapGetters('nutritionStepper', ['nutritionIrrigationData']),
			hasAnySectors() {
				if (!this.fertilization?.contribution_products) return false;
				return this.fertilization.contribution_products.some(
					(product) =>
						Array.isArray(product.periods_sectors) &&
						product.periods_sectors.some(
							(sectors) => this.normalizeSectors(sectors).length > 0,
						),
				);
			},
			getPeriods() {
				const { timeValues } = getTimeValues(
					moment(this.fertilization.init_date),
					moment(this.fertilization.end_date),
					this.fertilization.period,
					this.isReferencePlan
				);

				const grouped = {};

				Object.entries(timeValues[0]).forEach(([name, value], idx) => {
					let year, month, label;
					if (this.fertilization.period === 'DIARIO') {
						const [day, mon, yr] = name.split('-');
						year = yr;
						month = moment(`${yr}-${mon}-${day}`)
							.locale('es-ES')
							.format('MMMM');
						label = day;
					} else if (this.fertilization.period === 'SEMANAL') {
						const [week, yr, ...monthParts] = name.split(' ');
						year = yr;
						month = formatMonthLabel(monthParts.join(' '));
						label = week;
					} else if (this.fertilization.period === 'MENSUAL') {
						const [mon, yr] = name.split(' - ');
						year = yr?.length === 2 ? `20${yr}` : yr;
						month = formatMonthLabel(mon);
						label = mon;
					}

					if (!grouped[year]) grouped[year] = {};
					if (!grouped[year][month]) grouped[year][month] = [];
					grouped[year][month].push({
						dataField: idx.toString(),
						caption: label,
						alignment: 'right',
						allowSorting: false,
						minWidth: 100,
						cellTemplate: 'dataCellTemplate',
					});
				});

				// Estructura jerárquica según tipo de periodo
				if (this.fertilization.period === 'MENSUAL') {
					// Solo año → mes (sin tercer nivel)
					return Object.entries(grouped).map(([year, months]) => ({
						caption: year,
						columns: sortMonths(months).flatMap(([, items]) => items),
					}));
				} else {
					// Año → mes → día/semana
					return Object.entries(grouped).map(([year, months]) => ({
						caption: year,
						columns: sortMonths(months).map(([month, items]) => ({
							caption: month,
							columns: items,
						})),
					}));
				}
			},
		},
		async mounted() {
			const products = [];

			this.fertilization.contribution_products.forEach((product) => {
				const isLiquid = product.presentation === 'LIQUIDO';
				let unit;

				if (this.fertilization.unit_dosis === 1) {
					unit = isLiquid ? 'L/ha' : 'Kg/ha';
				} else {
					unit = isLiquid ? 'cm3/pie' : 'g/pie';
				}

				const name = `${product.product_name} (${unit})`;

				// Fila de dosis
				const productDose = { name, rowType: 'dosis' };

				product.periods.forEach((period, index) => {
					productDose[index] =
						period != null && period != 0 ? this.fixNumberTo(period, 1) : null;
				});

				products.push(productDose);

				// Fila de sectores (solo si este producto tiene sectores)
				const hasSectorsInProduct =
					Array.isArray(product.periods_sectors) &&
					product.periods_sectors.some(
						(sectors) => this.normalizeSectors(sectors).length > 0,
					);

				if (hasSectorsInProduct) {
					const productSectors = { name, rowType: 'sectores' };

					if (product.periods_sectors && Array.isArray(product.periods_sectors)) {
						product.periods_sectors.forEach((sectors, index) => {
							productSectors[index] = this.formatSectorsForDisplay(sectors);
						});
					}

					products.push(productSectors);
				}
			});

			if (this.fertilization.period === 'DIARIO') {
				// Agregar fila de tiempo de riego solo para planes diarios

				// Construir fila de tiempo de riego usando nutritionIrrigationData.data
				const irrigationRow = { name: 'Riego (h)', rowType: '' };
				let irrigationTimes = [];

				if (
					this.nutritionIrrigationData &&
					Array.isArray(this.nutritionIrrigationData.data)
				) {
					// Recorrer años, meses y fechas
					this.nutritionIrrigationData.data.forEach((yearObj) => {
						yearObj.items.forEach((monthObj) => {
							Object.entries(monthObj.dates).forEach(([date, info]) => {
								irrigationTimes.push(info.irrigation_time);
							});
						});
					});
				}

				const formattedIrrigationTimes = irrigationTimes.map((time) =>
					this.formatIrrigationTimeForDisplay(time),
				);

				const hasIrrigationTime = formattedIrrigationTimes.some(
					(value) => value !== '',
				);

				if (hasIrrigationTime) {
					// Asignar los tiempos de riego en el mismo orden que los periodos
					formattedIrrigationTimes.forEach((time, index) => {
						irrigationRow[index] = time;
					});

					products.push(irrigationRow);
				}
			}
			this.dataSource = products;
		},
		methods: {
			getRawCellValue(cellData) {
				const dataField = cellData?.column?.dataField;
				if (dataField === undefined || dataField === null) {
					return cellData?.value;
				}

				if (cellData?.data && Object.prototype.hasOwnProperty.call(cellData.data, dataField)) {
					return cellData.data[dataField];
				}

				return cellData?.value;
			},
			getSectorsForCell(value) {
				return this.normalizeSectors(value);
			},
			normalizeSectors(sectors) {
				if (sectors === null || sectors === undefined) return [];

				if (Array.isArray(sectors)) {
					return sectors
						.flatMap((item) => this.normalizeSectors(item))
						.filter((item) => item !== '');
				}

				if (typeof sectors === 'number' && Number.isFinite(sectors)) {
					return [sectors];
				}

				if (typeof sectors === 'object') {
					if (Array.isArray(sectors.sectores)) {
						return this.normalizeSectors(sectors.sectores);
					}
					if (Array.isArray(sectors.sectors)) {
						return this.normalizeSectors(sectors.sectors);
					}

					const values = Object.values(sectors);
					if (values.length > 0) {
						return this.normalizeSectors(values);
					}

					return [];
				}

				if (typeof sectors === 'string') {
					const normalized = sectors.trim();
					if (normalized === '') return [];

					if (normalized.toLowerCase() === 'todos') return ['todos'];

					if (
						(normalized.startsWith('[') && normalized.endsWith(']')) ||
						(normalized.startsWith('{') && normalized.endsWith('}'))
					) {
						try {
							const parsed = JSON.parse(normalized);
							return this.normalizeSectors(parsed);
						} catch (error) {
							// fallback a split por comas
						}
					}

					if (normalized.includes(',')) {
						return normalized
							.split(',')
							.flatMap((item) => this.normalizeSectors(item));
					}

					if (normalized.includes(';')) {
						return normalized
							.split(';')
							.flatMap((item) => this.normalizeSectors(item));
					}

					const numericValue = Number(normalized);
					if (!Number.isNaN(numericValue)) {
						return [numericValue];
					}

					return [normalized];
				}

				return [];
			},
			fixNumberTo(number, digits) {
				return parseFloat(parseFloat(number).toFixed(digits));
			},
			formatIrrigationTimeForDisplay(value) {
				if (value === null || value === undefined || value === '') return '';

				const formatDecimalHours = (rawValue) => {
					const normalized = String(rawValue).trim().replace(',', '.');
					const numericValue = Number.parseFloat(normalized);
					if (!Number.isFinite(numericValue)) return null;

					const safeValue = Math.max(numericValue, 0);
					let hours = Math.trunc(safeValue);
					let minutes = Math.round((safeValue - hours) * 60);

					if (minutes === 60) {
						hours += 1;
						minutes = 0;
					}

					return `${hours}:${String(minutes).padStart(2, '0')}`;
				};

				if (typeof value === 'string') {
					const normalizedValue = value.trim();
					if (normalizedValue === '') return '';
					if (normalizedValue.includes(':')) return normalizedValue;

					const decimalFormatted = formatDecimalHours(normalizedValue);
					if (decimalFormatted) return decimalFormatted;

					return normalizedValue;
				}

				if (typeof value === 'number' && Number.isFinite(value)) {
					const decimalFormatted = formatDecimalHours(value);
					if (decimalFormatted) return decimalFormatted;
				}

				return String(value);
			},
			formatSectorsForDisplay(sectors, totalSectors = 25) {
				const normalizedSectors = this.normalizeSectors(sectors);

				if (normalizedSectors.length === 0) {
					return [];
				}
				if (normalizedSectors.length === totalSectors) {
					return ['todos'];
				}
				return [...new Set(normalizedSectors)];
			},
			onCellPreparedViewing(e) {
				if (e.rowType === 'data') {
					// Detecta la fila de "Riego (h)" por su nombre
					if (e.data && e.data.name === 'Riego (h)') {
						e.cellElement.style.backgroundColor = 'var(--fr-chocolat)';
						e.cellElement.style.borderTop = '2px solid #ddd';
						e.cellElement.style.fontWeight = '600';
					}
					if (e.data && e.data.rowType === 'sectores') {
						e.cellElement.style.whiteSpace = 'normal';
						e.cellElement.style.overflow = 'visible';
						e.cellElement.style.textOverflow = 'clip';
						e.cellElement.style.height = 'auto';
						e.cellElement.style.lineHeight = 'normal';

						const prevRow = this.dataSource[e.rowIndex - 1];
						if (
							prevRow &&
							prevRow.rowType === 'dosis' &&
							prevRow.name === e.data.name
						) {
							e.cellElement.style.borderTop = 'none';
						}
					}

					if (e.data && e.data.rowType === 'dosis') {
						const nextRow = this.dataSource[e.rowIndex + 1];
						if (
							nextRow &&
							nextRow.rowType === 'sectores' &&
							nextRow.name === e.data.name
						) {
							e.cellElement.style.borderBottom = 'none';
						}
					}
				}
			},
		},
	};
</script>

<style scoped>
	.grid-application-efficiency-per-period::v-deep
		.dx-header-row
		td[role='columnheader'] {
		text-transform: capitalize;
	}

	.sectors-container {
		display: flex;
		flex-wrap: wrap;
		gap: 4px;
		justify-content: flex-end;
	}

	.sector-box {
		background: #EBEBEB;
		border-radius: 6px;
		padding: 2px 6px;
		font-size: 11px;
	}
</style>
